import { useState } from "react";
import {
  Drop,
  Calculator,
  ChatCircleText,
  Waveform,
  Warning,
  ChartLine,
  Heartbeat,
  Pill,
  BookOpen,
  Pulse,
  Syringe,
  GraduationCap,
  FolderOpen,
  Brain,
  CaretDown,
  ArrowRight,
} from "@phosphor-icons/react";
import { APP_STORE_URL } from "@/lib/constants";
import { trackEvent } from "@/lib/analytics";

/**
 * SmartTools — the two-tier section that reframes the 53-tool app:
 *   Tier 1: the 14 AI-powered / cross-cutting tools that are the real
 *           differentiator vs. Epocrates / Micromedex (the moat)
 *   Tier 2: the 39 static references + calculators, listed compactly by
 *           category so nothing is excluded but nothing is glorified
 *
 * Copy is neutral (no first-person, no promises of frequency).
 */

const SMART_TOOLS = [
  {
    id: "drips",
    name: "Drip Calculator",
    category: "Drips",
    icon: <Drop size={20} weight="duotone" />,
    story: "Titrating norepi at 8 mL/hr — is that inside a safe mcg/kg/min? AI double-checks.",
  },
  {
    id: "scores",
    name: "Scores & Calculators",
    category: "Bedside",
    icon: <Calculator size={20} weight="duotone" />,
    story: "15 bedside scores in one tap — GCS, MEWS, NEWS2, NIHSS, CIWA, COWS, and more.",
  },
  {
    id: "sbar",
    name: "SBAR Handoff",
    category: "Workflow",
    icon: <ChatCircleText size={20} weight="duotone" />,
    story: "Dictate the story. Get a clean, structured SBAR ready for the oncoming nurse.",
  },
  {
    id: "abg",
    name: "ABG Interpreter",
    category: "Labs",
    icon: <Waveform size={20} weight="duotone" />,
    story: "pH 7.28, pCO₂ 52, HCO₃ 24 — mixed picture? Anion gap open? A-a gradient wide?",
  },
  {
    id: "ysite",
    name: "Y-Site Compatibility",
    category: "Drips",
    icon: <Warning size={20} weight="duotone" />,
    story: "Vasopressor plus sedation drip in the same line — safe or hard incompatibility?",
  },
  {
    id: "labtrend",
    name: "Explain Lab Trend",
    category: "Labs",
    icon: <ChartLine size={20} weight="duotone" />,
    story: "Creatinine climbing 3 days running. What's the trajectory suggesting?",
  },
  {
    id: "ecg",
    name: "ECG Interpretation",
    category: "Cardiac",
    icon: <Heartbeat size={20} weight="duotone" />,
    story: "Snap the strip. Get the read — rhythm, rate, red flags called out plainly.",
  },
  {
    id: "interactions",
    name: "Drug Interactions",
    category: "Pharmacology",
    icon: <Pill size={20} weight="duotone" />,
    story: "Amio + apixaban + azithro — QT prolongation? Bleeding risk? Multi-drug check.",
  },
  {
    id: "handouts",
    name: "Education Handouts",
    category: "Teaching",
    icon: <BookOpen size={20} weight="duotone" />,
    story: "Discharge teaching in seconds — matched to the patient's reading level.",
  },
  {
    id: "hemo",
    name: "Hemodynamics",
    category: "Cardiac",
    icon: <Pulse size={20} weight="duotone" />,
    story: "MAP, BSA, CO/CI, SVR, PVR — live calculations as the numbers change.",
  },
  {
    id: "sedation",
    name: "Moderate Sedation",
    category: "Procedural",
    icon: <Syringe size={20} weight="duotone" />,
    story: "Aldrete scoring and the procedural sedation sheet, ready to document.",
  },
  {
    id: "ce",
    name: "CE Tracker",
    category: "Career",
    icon: <GraduationCap size={20} weight="duotone" />,
    story: "Track CEUs across states. AI-suggested topics tuned to your specialty.",
  },
  {
    id: "protocols",
    name: "My Hospital Protocols",
    category: "RAG",
    icon: <FolderOpen size={20} weight="duotone" />,
    story: "Upload the unit's PDFs. Ask them like a colleague who's read every page.",
  },
  {
    id: "sim",
    name: "Simulation Scenarios",
    category: "Training",
    icon: <Brain size={20} weight="duotone" />,
    story: "Practice a code, sepsis, or trauma case with a Claude-coached debrief.",
  },
];

// Flat category groups of the 39 reference tools. Order = clinical
// familiarity, not alphabet. Each entry is intentionally terse — this
// tier's job is to reassure "the depth is here" without glorifying
// any single item.
const REFERENCE_GROUPS = [
  {
    label: "Codes & Resuscitation",
    items: [
      "Adult Cardiac Arrest Quick-Reference",
      "Pediatric Resuscitation Quick-Reference",
      "Code Medications (ACLS)",
      "Rapid Sequence Intubation",
    ],
  },
  {
    label: "Cardiac",
    items: [
      "Cardiac Dysrhythmias",
      "Vasopressors Cheat Sheet",
      "Common ICU Drip Titration",
    ],
  },
  {
    label: "Emergencies",
    items: [
      "Sepsis Hour-1 Bundle",
      "Anaphylaxis (Deep Dive)",
      "Stroke — BE-FAST + tPA",
      "Trauma Resuscitation (Primary & Secondary)",
      "Trauma Nursing Survey (TNCC)",
      "Burns — TBSA + Parkland",
      "Toxicology + Antidotes",
      "GI Bleed",
      "OB Emergencies",
      "Behavioral / Psych Emergency",
      "Endocrine Emergencies",
    ],
  },
  {
    label: "Pediatrics",
    items: ["Pediatric Emergency", "Peds Dose Calculator", "PEWS"],
  },
  {
    label: "Labs & Assessment",
    items: [
      "Lab Interpretation Helper",
      "Electrolyte Replacement",
      "Bedside Scales",
      "ESI Triage (1–5)",
      "Ventilator Basics",
    ],
  },
  {
    label: "Score Calculators",
    items: [
      "GCS (Glasgow Coma Scale)",
      "NIHSS (stroke severity)",
      "MEWS",
      "NEWS2",
      "Parkland Formula (burns)",
      "CIWA (alcohol withdrawal)",
      "COWS (opioid withdrawal)",
      "CrCl / eGFR",
      "Anion Gap",
      "Electrolyte Corrections",
      "IBW / ABW",
      "Alcohol Teaching Handout",
    ],
  },
];

export default function SmartTools() {
  const [showAllReferences, setShowAllReferences] = useState(false);

  const visibleGroups = showAllReferences
    ? REFERENCE_GROUPS
    : REFERENCE_GROUPS.slice(0, 3);

  return (
    <section
      id="smart-tools"
      data-testid="smart-tools"
      className="relative bg-[#050505] border-t border-white/10 noise"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-24 md:py-32">
        {/* Header */}
        <div className="max-w-3xl">
          <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#FF2A2A] mb-5">
            § 03 · what&rsquo;s inside
          </div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl xl:text-[72px] font-extrabold tracking-tighter leading-[0.95]">
            50+ tools.{" "}
            <span className="italic font-light text-white/70">
              One app.
            </span>
          </h2>
          <p className="mt-6 text-base md:text-lg text-white/65 max-w-2xl leading-relaxed">
            From code drugs to calculators, protocols to patient teaching, and
            everything nurses reach for in between. Here&rsquo;s a look at
            fourteen of the tools most-used at bedside.
          </p>
        </div>

        {/* Smart tools grid (14) */}
        <div
          className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
          data-testid="smart-tools-grid"
        >
          {SMART_TOOLS.map((tool) => (
            <SmartToolCard key={tool.id} tool={tool} />
          ))}
        </div>

        {/* Reference library — flat, no hierarchy */}
        <div
          className="mt-20 pt-12 border-t border-white/10"
          data-testid="reference-library"
        >
          <div className="flex flex-wrap items-baseline justify-between gap-3 mb-8">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/45 mb-2">
                § 03.5 · the rest of the toolkit
              </div>
              <h3 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">
                Everything else, one tap away.
              </h3>
            </div>
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/40 max-w-xs">
              // codes · calculators · protocols · references
            </p>
          </div>

          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-8"
            data-testid="reference-library-groups"
          >
            {visibleGroups.map((group) => (
              <div key={group.label}>
                <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#FF2A2A]/90 mb-3">
                  {group.label}
                </div>
                <ul className="space-y-1.5">
                  {group.items.map((item) => (
                    <li
                      key={item}
                      className="font-mono text-[11px] text-white/60 hover:text-white/90 transition-colors leading-relaxed"
                    >
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {REFERENCE_GROUPS.length > 3 ? (
            <button
              type="button"
              onClick={() => setShowAllReferences((s) => !s)}
              data-testid="reference-library-toggle"
              className="mt-10 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-white/55 hover:text-[#FF2A2A] transition-colors"
            >
              <CaretDown
                size={14}
                weight="bold"
                className={`transition-transform ${showAllReferences ? "rotate-180" : ""}`}
              />
              {showAllReferences
                ? "collapse reference library"
                : `show all ${REFERENCE_GROUPS.length} categories`}
            </button>
          ) : null}
        </div>

        {/* Section-close CTA */}
        <div className="mt-16 pt-10 border-t border-white/10 flex flex-wrap items-center justify-between gap-4">
          <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-white/50 max-w-md">
            // 50+ tools &middot; one 14-day free trial
          </p>
          <a
            href={APP_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() =>
              trackEvent("download_click", "smart-tools-section", {
                store: "apple",
              })
            }
            data-testid="smart-tools-cta"
            className="group inline-flex items-center gap-3 bg-[#FF2A2A] hover:bg-[#FF4D4D] text-white font-display font-bold text-lg tracking-tight px-7 py-4 transition-colors"
          >
            <span>Try it free for 14 days</span>
            <ArrowRight
              size={20}
              weight="bold"
              className="transition-transform group-hover:translate-x-1"
            />
          </a>
        </div>
      </div>
    </section>
  );
}

function SmartToolCard({ tool }) {
  return (
    <div
      className="group border border-white/10 hover:border-[#FF2A2A]/50 bg-[#0a0a0a] hover:bg-[#0a0a0a] p-6 transition-colors"
      data-testid={`smart-tool-${tool.id}`}
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="w-10 h-10 shrink-0 flex items-center justify-center border border-[#FF2A2A]/30 bg-[#FF2A2A]/[0.06] text-[#FF2A2A] group-hover:bg-[#FF2A2A]/10 transition-colors">
          {tool.icon}
        </div>
        <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-white/40 pt-2">
          {tool.category}
        </div>
      </div>
      <h4 className="font-display text-xl font-extrabold tracking-tight text-white leading-tight">
        {tool.name}
      </h4>
      <p className="mt-2 text-sm text-white/60 leading-relaxed">
        {tool.story}
      </p>
    </div>
  );
}
